import { useContext, useEffect, useRef, useState } from "react";
import { Task } from "protogen/tasks_pb";
import { Box, Typography, Button, ButtonGroup } from "@mui/material";
import { PRIORITY_STATES } from "../creation/PrioritySelect";
import { FilterContext, TaskFilter, TaskSort } from "./FilterContext";
import useIsMobile from "../hooks/useIsMobile";
import { ReactComponent as EllipseIcon } from "../../icons/Menu/Ellipse.svg";
import { Download } from "lucide-react";
import {
  AvailableFilterOptionsAdvisor,
  StageOptions,
  StageValue,
} from "./workflowStates";
import CheckedDropDown from "../common/CheckedDropDown";
import EmptyTableOverlay from "../common/EmptyTableOverlay";
import { Check } from "lucide-react";
import { TIME_RANGES } from "components/creation/TimeRangeSelect";
import TaskRow from "./TaskRow";
import { useListTaskProjects } from "services/tasks";
import { ListTaskProjectsRequest } from "protogen/tasks_service_pb";
import {
  TASK_PAGER_STATE_KEY,
  TaskPagerContext,
} from "components/tasks/taskPagerContext";

const Indicator = () => {
  return <EllipseIcon height={6} width={6} style={{ marginLeft: "4px" }} />;
};

const TaskFilters = ({
  families,
  onFilterChange,
  showFamilyFilter = true,
  showProjectsFilter = false,
  familyRef,
}: {
  families: { label: string; value: string }[];
  onFilterChange: (field: string, values: string[]) => void;
  showFamilyFilter?: boolean;
  showProjectsFilter?: boolean;
  familyRef?: string;
}) => {
  const { filters, upsertFilter, updateSorts, sorts } =
    useContext(FilterContext);
  const isMobile = useIsMobile();
  const { request: listTaskProjectsRequest } = useListTaskProjects();

  // Get current filter values from context
  const selectedPriorities =
    filters.find((f) => f.field === "priority")?.value || [];
  const selectedFamilies =
    filters.find((f) => f.field === "family")?.value || [];
  const selectedStages = filters.find((f) => f.field === "stage")?.value || [];
  const selectedTimeRange =
    filters.find((f) => f.field === "timeRange")?.value || null;
  const selectedProjects =
    filters.find((f) => f.field === "project")?.value || [];

  const priorityOptions = [
    ...PRIORITY_STATES.map((p) => ({
      label: p.label,
      value: p.value,
      mutuallyExclusive: false,
    })),
  ];
  const [initialSelectedPriorities] = useState<string[]>(
    selectedPriorities.length ? selectedPriorities : [],
  );

  const timeRangeOptions = [
    ...Object.values(TIME_RANGES).map((p) => ({
      label: p.label,
      value: p.value || "",
      mutuallyExclusive: true,
    })),
  ];
  const [initialSelectedTimeRange] = useState<string | null>(
    selectedTimeRange || null,
  );

  const stageOptions = [
    ...AvailableFilterOptionsAdvisor.map((s) => ({
      label: s.label,
      value: s.value,
      labelElement: s.labelElement,
      mutuallyExclusive: false,
    })),
  ];
  const [initialSelectedStages] = useState<string[]>(
    selectedStages.length ? selectedStages : [],
  );

  const familyOptions = [
    ...families
      .sort((a, b) => a.label.localeCompare(b.label))
      .map((f) => ({
        label: f.label,
        value: f.value,
        mutuallyExclusive: false,
        defaultChecked: true,
      })),
  ];
  const [initialSelectedFamilies] = useState<string[]>(
    selectedFamilies.length ? selectedFamilies : [],
  );

  const [projectOptions, setProjectOptions] = useState<
    { label: string; value: string; mutuallyExclusive: boolean }[]
  >([]);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [initialSelectedProjects] = useState<string[]>(
    selectedProjects.length ? selectedProjects : [],
  );

  // Load projects when component mounts if showProjectsFilter is true
  useEffect(() => {
    if (showProjectsFilter && familyRef && !projectsLoaded) {
      const loadProjects = async () => {
        const response = await listTaskProjectsRequest(
          new ListTaskProjectsRequest({
            familyRef: familyRef,
          }),
        );
        if (response) {
          // Combine community and family projects, removing duplicates
          const allProjects = Array.from(new Set([...response?.projects]));
          const options = allProjects.map((project) => ({
            label: project,
            value: project,
            mutuallyExclusive: false,
          }));
          setProjectOptions(options);
        }
        setProjectsLoaded(true);
      };
      loadProjects();
    }
  }, [showProjectsFilter, familyRef, projectsLoaded]);

  const handlePriorityChange = (
    selected: { label: string; value: string }[],
  ) => {
    let values = selected.map((s) => s.value).filter((v) => v !== "");
    if (values.length === priorityOptions.length) {
      values = [];
    }
    upsertFilter({
      field: "priority",
      operator: "oneOf",
      value: values,
    });
    onFilterChange("priority", values);
  };

  const handleStageChange = (selected: { label: string; value: string }[]) => {
    let values = selected.map((s) => s.value).filter((v) => v !== "");
    upsertFilter({
      field: "stage",
      operator: "oneOf",
      value: values,
    });
    onFilterChange("stage", values);
  };

  const handleFamilyChange = (selected: { label: string; value: string }[]) => {
    let values = selected.map((s) => s.value).filter((v) => v !== "");
    if (values.length === familyOptions.length) {
      values = [];
    }
    upsertFilter({
      field: "family",
      operator: "oneOf",
      value: values,
    });
    onFilterChange("family", values);
  };
  const handleTimeRangeChange = (
    selected: { label: string; value: string }[],
  ) => {
    const value = selected.length > 0 ? selected[0].value : null;
    upsertFilter({
      field: "timeRange",
      operator: "greaterThan",
      value: value,
    });
    onFilterChange("timeRange", value ? [value] : []);
  };
  const handleProjectChange = (
    selected: { label: string; value: string }[],
  ) => {
    let values = selected.map((s) => s.value).filter((v) => v !== "");
    upsertFilter({
      field: "project",
      operator: "oneOf",
      value: values,
    });
    onFilterChange("project", values);
  };
  const handleSortChange = (selected: { label: string; value: string }[]) => {
    const newSorts = selected
      .map((s) => s.value)
      .filter((v) => v !== "")
      .map((v) => ({
        field: v.split("-")[0],
        descending: v.split("-")[1] === "desc",
      }));
    updateSorts(newSorts);
  };
  const doneFilter =
    selectedStages.length === 1 && selectedStages[0] === StageValue.Done;
  return (
    <Box
      sx={{
        display: "flex",
        gap: "12px",
        flexWrap: "wrap",
        alignItems: isMobile ? "end" : "center",
        marginBottom: "16px",
        justifyContent: "space-between",
        flexDirection: isMobile ? "column" : "row",
      }}
    >
      <ButtonGroup
        sx={{
          display: "flex",
          ...(isMobile && {
            marginBottom: "16px",
            width: "100%",
          }),
        }}
      >
        <Button
          sx={{ flex: "1", width: "100%" }}
          variant={!doneFilter ? "buttonGroupSelected" : "buttonGroup"}
          // set to...all statuses
          onClick={() => {
            handleStageChange([]);
          }}
        >
          Open
        </Button>
        <Button
          sx={{ flex: "1", width: "100%" }}
          variant={doneFilter ? "buttonGroupSelected" : "buttonGroup"}
          onClick={() => {
            handleStageChange([{ label: "Done", value: StageValue.Done }]);
          }}
        >
          Done
        </Button>
      </ButtonGroup>
      <Box>
        {!doneFilter && (
          <CheckedDropDown
            sx={{
              "& .MuiSelect-select": {
                paddingLeft: "0px !important",
              },
            }}
            sxPaper={{
              "li + li": {
                marginTop: "10px",
              },
            }}
            disabled={doneFilter}
            options={stageOptions}
            initial={initialSelectedStages}
            onChange={(selected) => {
              handleStageChange(selected);
            }}
            displayValue={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                Status
                {selectedStages.length ? <Indicator /> : undefined}
              </Box>
            }
            noOutline={true}
          />
        )}
        {showFamilyFilter && (
          <CheckedDropDown
            key={`family-${initialSelectedFamilies.join(",")}`}
            sx={{
              "& .MuiSelect-select": {
                paddingLeft: "0px !important",
              },
            }}
            options={familyOptions}
            initial={initialSelectedFamilies}
            onChange={handleFamilyChange}
            displayValue={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                Family
                {selectedFamilies.length > 0 && <Indicator />}
              </Box>
            }
            noOutline={true}
          />
        )}
        {showProjectsFilter && (
          <CheckedDropDown
            sx={{
              "& .MuiSelect-select": {
                paddingLeft: "0px !important",
              },
            }}
            options={projectOptions}
            initial={initialSelectedProjects}
            onChange={handleProjectChange}
            disabled={projectsLoaded && projectOptions.length === 0}
            displayValue={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {projectsLoaded && projectOptions.length === 0
                  ? "No projects"
                  : "Project"}
                {selectedProjects.length > 0 && <Indicator />}
              </Box>
            }
            noOutline={true}
          />
        )}
        {!isMobile && (
          <CheckedDropDown
            sx={{
              "& .MuiSelect-select": {
                paddingLeft: "0px !important",
              },
            }}
            options={priorityOptions}
            initial={initialSelectedPriorities}
            onChange={handlePriorityChange}
            displayValue={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                Priority
                {selectedPriorities.length > 0 &&
                  selectedPriorities.length !== priorityOptions.length && (
                    <Indicator />
                  )}
              </Box>
            }
            noOutline={true}
          />
        )}
        <CheckedDropDown
          sx={{
            "& .MuiSelect-select": {
              paddingLeft: "0px !important",
            },
          }}
          options={timeRangeOptions}
          initial={initialSelectedTimeRange ? [initialSelectedTimeRange] : []}
          radio={true}
          onChange={handleTimeRangeChange}
          displayValue={
            <Box sx={{ display: "flex", alignItems: "center" }}>
              Created
              {selectedTimeRange && <Indicator />}
            </Box>
          }
          noOutline={true}
        />
        <CheckedDropDown
          closeOnSelect={true}
          sx={{
            "& .MuiSelect-select": {
              paddingLeft: "0px !important",
            },
          }}
          options={[
            {
              label: "Due date ascending",
              value: "dueDate-asc",
              mutuallyExclusive: true,
            },
            {
              label: "Due date descending",
              value: "dueDate-desc",
              mutuallyExclusive: true,
            },
            {
              label: "Priority ascending",
              value: "priority-asc",
              mutuallyExclusive: true,
            },
            {
              label: "Priority descending",
              value: "priority-desc",
              mutuallyExclusive: true,
            },
            {
              label: "Last activity ascending",
              value: "lastActivity-asc",
              mutuallyExclusive: true,
            },
            {
              label: "Last activity descending",
              value: "lastActivity-desc",
              mutuallyExclusive: true,
            },
            ...(doneFilter
              ? [
                  {
                    label: "Completed date ascending",
                    value: "completedDate-asc",
                    mutuallyExclusive: true,
                  },
                  {
                    label: "Completed date descending",
                    value: "completedDate-desc",
                    mutuallyExclusive: true,
                  },
                ]
              : []),
          ]}
          ActiveIcon={Check}
          // initial={initialSelectedSort}
          onChange={handleSortChange}
          displayValue={
            <Box sx={{ display: "flex", alignItems: "center" }}>
              Sort
              {sorts.length > 0 && <Indicator />}
            </Box>
          }
          noOutline={true}
        />
      </Box>
    </Box>
  );
};

/**
 * Default task sorting algorithm - prioritizes actionability and urgency.
 *
 * Sort hierarchy (each level is a tie-breaker for the previous):
 * 1. Unread status count - Tasks with unread updates surface first
 * 2. Stage priority - Ordered by actionability:
 *    - New (1): Needs initial triage
 *    - AdvisorReview (2): Requires advisor attention
 *    - Active (3): Currently being worked
 *    - OnHold with date <= today (4): Hold period expired, needs action
 *    - InQueue (5): Queued and ready to be worked
 *    - WaitingOnClient (6): Blocked on external response
 *    - OnHold future/no date (7): Intentionally deferred
 *    - Done (8), Deleted (9): Terminal/inactive states
 * 3. Priority value - High > Medium > Low
 * 4. Due date - Tasks with due dates first, then by earliest date
 * 5. Creation date - Older tasks first (FIFO for equal priority)
 */
export const defaultTaskSorter = (a: Task, b: Task) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset to start of day for comparison

  // Helper function to get stage priority
  const getStagePriority = (task: Task): number => {
    const { stage, stageDate } = task;

    switch (stage) {
      case StageValue.New:
        return 1;
      case StageValue.AdvisorReview:
        return 2;
      case StageValue.InQueue:
        return 5;
      case StageValue.OnHold:
        // Special handling for OnHold with stage dates
        if (stageDate) {
          const stageDateObj = new Date(stageDate);
          stageDateObj.setHours(0, 0, 0, 0);
          if (stageDateObj <= today) {
            return 4; // OnHold with date <= today
          } else {
            return 7; // OnHold in the future
          }
        }
        return 7; // OnHold without date (treat as future)
      case StageValue.Active:
        return 3;
      case StageValue.WaitingOnClient:
        return 6;
      case StageValue.Done:
        return 8;
      case StageValue.Deleted:
        return 9;
      default:
        return 10;
    }
  };

  if (a.unreadStatusCount !== b.unreadStatusCount) {
    return b.unreadStatusCount - a.unreadStatusCount;
  }

  // Compare by stage priority first
  const aPriority = getStagePriority(a);
  const bPriority = getStagePriority(b);

  if (aPriority !== bPriority) {
    return aPriority - bPriority;
  }

  // Tie-breaker 0: Priority (higher priority comes first)
  const aPriorityValue = priorityValue(a.priority);
  const bPriorityValue = priorityValue(b.priority);
  if (aPriorityValue !== bPriorityValue) {
    return bPriorityValue - aPriorityValue;
  }

  // Tie-breaker 1: Due date (presence of due date takes priority, then by date)
  const aHasDueDate = !!a.dueDate;
  const bHasDueDate = !!b.dueDate;

  if (aHasDueDate && !bHasDueDate) {
    return -1; // a has due date, b doesn't - a comes first
  }
  if (!aHasDueDate && bHasDueDate) {
    return 1; // b has due date, a doesn't - b comes first
  }
  if (aHasDueDate && bHasDueDate) {
    const aDate = new Date(a.dueDate!);
    const bDate = new Date(b.dueDate!);
    if (aDate.getTime() !== bDate.getTime()) {
      return aDate.getTime() - bDate.getTime(); // Earlier due date first
    }
  }

  // Tie-breaker 2: Creation date (earlier creation date first)
  return Number(a.createdSec) - Number(b.createdSec);
};

const priorityValue = (priority: string) => {
  switch (priority.toLowerCase()) {
    case "high":
      return 3;
    case "medium":
      return 2;
    case "low":
      return 1;
    default:
      return 0;
  }
};

/**
 * User-selected sorting - applies custom sort fields in order.
 * Each sort field acts as a tie-breaker for the previous.
 * When no custom sorts are applied, defaultTaskSorter is used alone.
 */
const taskSorter = (a: Task, b: Task, sorts: TaskSort[]) => {
  for (const sort of sorts) {
    let aValue: any, bValue: any;

    switch (sort.field) {
      case "dueDate":
        aValue = a.dueDate ? new Date(a.dueDate).getTime() : 0;
        bValue = b.dueDate ? new Date(b.dueDate).getTime() : 0;
        break;
      case "priority":
        aValue = priorityValue(a.priority);
        bValue = priorityValue(b.priority);
        break;
      case "family":
        aValue = a.family?.name || "";
        bValue = b.family?.name || "";
        break;
      case "status":
        aValue = a.state || 0;
        bValue = b.state || 0;
        break;
      case "createdAt":
        aValue = a.createdSec || 0;
        bValue = b.createdSec || 0;
        break;
      case "completedDate":
        aValue = a.completionDateSec || 0;
        bValue = b.completionDateSec || 0;
        break;
      case "lastActivity":
        aValue = a.lastUpdatedSec || 0;
        bValue = b.lastUpdatedSec || 0;
        break;
      default:
        continue;
    }

    let comparison = 0;
    if (aValue < bValue) comparison = -1;
    if (aValue > bValue) comparison = 1;

    if (sort.descending) comparison = -comparison;

    if (comparison !== 0) return comparison;
  }
  return 0;
};

const formatDate = (sec: number | bigint): string => {
  const n = Number(sec);
  if (!n) return "";
  return new Date(n * 1000).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const escapeCsvField = (value: string): string => {
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

const generateTasksCsv = (tasks: Task[]): string => {
  const headers = [
    "Title",
    "Family",
    "Priority",
    "Stage",
    "Assignee",
    "Due Date",
    "Project",
    "Created",
    "Last Updated",
    "Completed",
  ];
  const rows = tasks.map((t) =>
    [
      t.title,
      t.family?.name || "",
      t.priority,
      StageOptions.find((s) => s.value === t.stage)?.advisorLabel || t.stage,
      t.assignee?.displayName || "",
      t.dueDate || "",
      t.project,
      formatDate(t.createdSec),
      formatDate(t.lastUpdatedSec),
      formatDate(t.completionDateSec),
    ].map(escapeCsvField),
  );
  return [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
};

const downloadCsv = (content: string, filename: string) => {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

type Props = {
  tasks: Task[];
  loading?: boolean;
  fetchTasks?: (f: TaskFilter[]) => void;
  hideStatus?: boolean;
  families?: { label: string; value: string }[];
  showFamilyFilter?: boolean;
  showProjectsFilter?: boolean;
  familyRef?: string;
  pageSize?: number;
  listName?: string;
  listPath?: string;
};

export default ({
  tasks,
  loading = false,
  fetchTasks,
  hideStatus = false,
  families = [],
  showFamilyFilter = true,
  showProjectsFilter = false,
  familyRef,
  pageSize = 20,
  listName,
  listPath,
}: Props) => {
  const { filters, sorts } = useContext(FilterContext);
  const isMobile = useIsMobile();
  const onFirstRender = useRef(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [displayedTasks, setDisplayedTasks] = useState<Task[]>([]);
  const [sortedTasks, setSortedTasks] = useState<Task[]>([]);

  useEffect(() => {
    if (onFirstRender.current) {
      onFirstRender.current = false;
      return;
    }
    fetchTasks && fetchTasks(filters);
  }, [filters]);

  useEffect(() => {
    let sorted = [...tasks];
    sorted.sort(defaultTaskSorter);

    if (sorts.length > 0) {
      sorted.sort((a, b) => taskSorter(a, b, sorts));
    }

    setSortedTasks(sorted);
  }, [tasks, sorts]);

  useEffect(() => {
    const startIndex = currentPage * pageSize;
    const endIndex = startIndex + pageSize;
    setDisplayedTasks(sortedTasks.slice(startIndex, endIndex));
  }, [sortedTasks, currentPage, pageSize]);

  const handleFilterChange = () => {
    setCurrentPage(0); // Reset to first page when filtering
  };

  const handleNextPage = () => {
    if ((currentPage + 1) * pageSize < sortedTasks.length) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const hasNextPage = (currentPage + 1) * pageSize < sortedTasks.length;
  const hasPreviousPage = currentPage > 0;

  const pagerContext: TaskPagerContext | undefined =
    listName && listPath
      ? {
          listName,
          listPath,
          entries: sortedTasks.map((t) => ({ ref: t.ref, title: t.title })),
        }
      : undefined;
  return (
    <Box sx={{ width: "100%" }}>
      <TaskFilters
        families={families}
        onFilterChange={handleFilterChange}
        showFamilyFilter={showFamilyFilter}
        showProjectsFilter={showProjectsFilter}
        familyRef={familyRef}
      />
      {loading ? (
        <Box
          sx={{ display: "flex", justifyContent: "center", padding: "40px" }}
        >
          <Typography>Loading...</Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {displayedTasks.length === 0 ? (
              <EmptyTableOverlay title="No Tasks" />
            ) : (
              displayedTasks.map((task) => (
                <TaskRow
                  key={task.ref}
                  task={task}
                  hideStatus={hideStatus}
                  navigationState={
                    pagerContext && { [TASK_PAGER_STATE_KEY]: pagerContext }
                  }
                />
              ))
            )}
          </Box>

          {sortedTasks.length > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "16px 0",
                marginTop: "16px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: isMobile ? "flex-start" : "center",
                  gap: "8px",
                  flexDirection:
                    isMobile && sortedTasks.length > pageSize
                      ? "column"
                      : "row",
                  ...(sortedTasks.length <= pageSize && {
                    marginLeft: "auto",
                  }),
                }}
              >
                {sortedTasks.length > pageSize && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <Typography variant="bodySmall" color="text.secondary">
                      Showing {currentPage * pageSize + 1}-
                      {Math.min(
                        (currentPage + 1) * pageSize,
                        sortedTasks.length,
                      )}{" "}
                      of {sortedTasks.length} tasks
                    </Typography>
                    {!isMobile && (
                      <Typography variant="bodySmall" color="text.secondary">
                        •
                      </Typography>
                    )}
                  </Box>
                )}
                <Button
                  variant="text"
                  size="small"
                  startIcon={<Download size={14} />}
                  onClick={() =>
                    downloadCsv(generateTasksCsv(sortedTasks), "tasks.csv")
                  }
                  sx={{ minWidth: "auto", height: "unset", fontWeight: "500" }}
                >
                  Download CSV
                </Button>
              </Box>
              {sortedTasks.length > pageSize && (
                <Box sx={{ display: "flex", gap: "8px" }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handlePreviousPage}
                    disabled={!hasPreviousPage}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleNextPage}
                    disabled={!hasNextPage}
                  >
                    Next
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};
